'use client';

import { useEffect } from 'react';

/**
 * DeferNonCriticalCSS - Client component to defer non-critical CSS loading
 * 
 * This component reduces render-blocking CSS by:
 * 1. Only deferring CSS that's added AFTER initial render (lazy-loaded component CSS)
 * 2. Using media="print" trick to load CSS without blocking render
 * 3. Switching to "all" media once loaded to apply styles
 * 
 * This improves LCP and FCP by allowing critical content to render first
 * while avoiding FOUC (Flash of Unstyled Content) for initial CSS
 */
export function DeferNonCriticalCSS() {
  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return;

    // Track initial stylesheets to avoid deferring critical CSS
    const initialStylesheets = new Set<string>();
    const initialLinks = document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]');
    initialLinks.forEach((link) => {
      if (link.href) {
        initialStylesheets.add(link.href);
      }
    });

    // Function to defer non-critical CSS (only CSS added after initial render)
    const deferNonCriticalCSS = (links: HTMLLinkElement[]) => {
      links.forEach((link) => {
        // Skip if already deferred, marked as critical, or was in initial render
        if (
          link.hasAttribute('data-deferred') ||
          link.hasAttribute('data-critical') ||
          (link.href && initialStylesheets.has(link.href))
        ) {
          return;
        }

        // Only defer CSS from async chunks (lazy-loaded components)
        // These are typically added after initial render and won't cause FOUC
        const isAsyncChunk = link.href && (
          link.href.includes('/_next/static/chunks/') ||
          link.href.includes('/_next/static/css/') && link.href.includes('async')
        );

        if (!isAsyncChunk) {
          return;
        }

        // Mark as deferred
        link.setAttribute('data-deferred', 'true');

        // Use media="print" trick to load CSS without blocking render
        const originalMedia = link.media || 'all';
        link.media = 'print';
        
        // Switch back to original media once loaded
        const switchMedia = () => {
          link.media = originalMedia;
        };

        link.onload = switchMedia;
        link.onerror = switchMedia;

        // Fallback: switch after a short delay
        setTimeout(() => {
          if (link.media === 'print') {
            link.media = originalMedia;
          }
        }, 100);
      });
    };

    // Watch for new stylesheets added after initial render
    if (window.MutationObserver) {
      const observer = new MutationObserver((mutations) => {
        const newLinks: HTMLLinkElement[] = [];
        
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (
              node.nodeType === 1 &&
              node instanceof HTMLLinkElement &&
              node.rel === 'stylesheet' &&
              node.href &&
              !initialStylesheets.has(node.href)
            ) {
              newLinks.push(node);
            }
          });
        });

        if (newLinks.length > 0) {
          // Defer CSS loading for dynamically added stylesheets
          deferNonCriticalCSS(newLinks);
        }
      });

      // Start observing after a short delay to catch dynamically loaded CSS
      const timeoutId = setTimeout(() => {
        if (document.head) {
          observer.observe(document.head, { childList: true, subtree: true });
        }
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        observer.disconnect();
      };
    }
  }, []);

  // This component doesn't render anything
  return null;
}
